import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line, Legend } from 'recharts';
import { getAnalyticsCharts } from '../../services/api';
import './DashboardCommon.css';
import smLogo from '../../assets/SM_logo-removebg.png';
import ClientList from '../ClientList';
import BrandList from '../BrandList';
import TenderList from '../TenderList';
import ParticipatingTenderList from '../ParticipatingTenderList';
import MasterTenderList from '../MasterTenderList';
import NoGoTenderList from '../NoGoTenderList';
import EmdList from '../EmdList';
import TenderKanban from '../TenderKanban';
import UpcomingDeadlinesWidget from '../UpcomingDeadlinesWidget';
import { BidValueStatsRow, PipelineValueChart, WinRateBySourceChart, WinRateByBuyerChart } from '../BidValueWidgets';
import ArchivedTenderList from '../ArchivedTenderList';
import MyAssignedTasks from '../MyAssignedTasks';
import ThemeToggle from '../ThemeToggle';
import NotificationBell from '../NotificationBell';

function SMTenderManagementDashboard({ user, onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [profileDropdown, setProfileDropdown] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(['tenderManagement']);
  const [dashboardData, setDashboardData] = useState({ stats: null, charts: null });
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);

  const fetchDashboardData = React.useCallback(() => {
    setDashboardLoading(true);
    setDashboardError(null);
    getAnalyticsCharts()
      .then((res) => {
        if (res.success) {
          setDashboardData({ stats: res.stats || {}, charts: res.charts || {} });
        }
      })
      .catch((err) => setDashboardError(err.message || 'Failed to load dashboard data.'))
      .finally(() => setDashboardLoading(false));
  }, []);

  useEffect(() => {
    if (activeMenu === 'dashboard') fetchDashboardData();
  }, [activeMenu, fetchDashboardData]);

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'home' },
    {
      id: 'tenderManagement',
      label: 'Tender Management',
      icon: 'file',
      children: [
        { id: 'pipeline', label: 'Pipeline Board', icon: 'columns' },
        { id: 'tenders', label: 'Tender Discovery', icon: 'search' },
        { id: 'participating', label: 'Participating', icon: 'check' },
        { id: 'won', label: 'Won', icon: 'trophy' },
        { id: 'lost', label: 'Lost', icon: 'x' },
        { id: 'dropped', label: 'Dropped', icon: 'minus' },
        { id: 'cancelled', label: 'Cancelled', icon: 'slash' },
        { id: 'master-list', label: 'Master List', icon: 'list' },
        { id: 'emds', label: 'EMDs', icon: 'briefcase' },
        { id: 'nogo', label: 'No Go Tenders', icon: 'x' },
        { id: 'archived', label: 'Archived', icon: 'archive' },
      ]
    },
    { id: 'myTasks', label: 'My Tasks', icon: 'checkSquare' },
    { id: 'emds', label: 'EMDs', icon: 'briefcase' }, 
    { id: 'brands', label: 'Brands & Products', icon: 'package' },
    { id: 'clients', label: 'Client List', icon: 'building' },
  ];

  const toggleMenuExpand = (menuId) => {
    setExpandedMenus(prev =>
      prev.includes(menuId)
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const getPageInfo = () => {
    if (activeMenu === 'dashboard') return { breadcrumb: [], title: 'Dashboard' };
    for (const item of menuItems) {
      if (item.children) {
        const child = item.children.find(c => c.id === activeMenu);
        if (child) return { breadcrumb: [item.label, child.label], title: child.label };
      } else if (item.id === activeMenu) {
        return { breadcrumb: [item.label], title: item.label };
      }
    }
    return { breadcrumb: [], title: '' };
  };

  const pageInfo = getPageInfo();

  const statsFromApi = dashboardData.stats;
  const stats = statsFromApi
    ? [
        { label: 'Tenders', value: String(statsFromApi.tender_total ?? '–'), change: 'Manage', color: '#b60000' },
        { label: 'Participating', value: String(statsFromApi.participating ?? '–'), change: 'Active', color: '#10b981' },
        { label: 'EMDs', value: String(statsFromApi.emd_total ?? '–'), change: 'Track', color: '#3b82f6' },
        { label: 'Upcoming Deadlines', value: String(statsFromApi.upcoming_deadlines ?? '–'), change: '7 days', color: '#6b7280' },
      ]
    : [
        { label: 'Tenders', value: '–', change: 'Manage', color: '#b60000' },
        { label: 'Participating', value: '–', change: 'Active', color: '#10b981' },
        { label: 'EMDs', value: '–', change: 'Track', color: '#3b82f6' },
        { label: 'Upcoming Deadlines', value: '–', change: '7 days', color: '#6b7280' },
      ];

  const renderIcon = (name) => {
    const icons = {
      home: <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>,
      search: <><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></>,
      check: <><polyline points="20 6 9 17 4 12"></polyline></>,
      x: <><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></>,
      file: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></>,
      chevronDown: <polyline points="6 9 12 15 18 9"></polyline>,
      chevronRight: <polyline points="9 18 15 12 9 6"></polyline>,
      building: <><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path><path d="M8 6h.01"></path><path d="M16 6h.01"></path><path d="M12 6h.01"></path><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path></>,
      briefcase: <><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></>,
      package: <><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></>,
      trophy: <><path d="M8 21h8"></path><path d="M12 17v4"></path><path d="M7 4h10v4a5 5 0 0 1-10 0V4z"></path><path d="M5 4H4a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h1"></path><path d="M19 4h1a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-1"></path><path d="M7 8a5 5 0 0 0 10 0"></path></>,
      minus: <><line x1="5" y1="12" x2="19" y2="12"></line></>,
      slash: <><circle cx="12" cy="12" r="10"></circle><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line></>,
      list: <><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></>,
      columns: <><line x1="12" y1="3" x2="12" y2="21"></line><rect x="3" y="3" width="7" height="18" rx="1"></rect><rect x="14" y="3" width="7" height="18" rx="1"></rect></>,
      logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></>,
      menu: <><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></>,
      bell: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></>,
      archive: <><polyline points="21 8 21 21 3 21 3 8"></polyline><rect x="1" y="3" width="22" height="5"></rect><line x1="10" y1="12" x2="14" y2="12"></line></>,
      checkSquare: <><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></>,
    };
    return icons[name] || null;
  };

  const renderContent = () => {
    switch (activeMenu) {
      case 'pipeline':
        return <TenderKanban />;
      case 'tenders':
        return <TenderList user={user} canManage={true} />;
      case 'participating':
        return <ParticipatingTenderList user={user} canManage={true} onGoToDiscovery={() => setActiveMenu('tenders')} />;
      case 'won':
        return <ParticipatingTenderList user={user} canManage={true} fixedStatus="won" onGoToDiscovery={() => setActiveMenu('tenders')} />;
      case 'lost':
        return <ParticipatingTenderList user={user} canManage={true} fixedStatus="lost" onGoToDiscovery={() => setActiveMenu('tenders')} />;
      case 'dropped':
        return <ParticipatingTenderList user={user} canManage={true} fixedStatus="dropped" onGoToDiscovery={() => setActiveMenu('tenders')} />;
      case 'cancelled':
        return <ParticipatingTenderList user={user} canManage={true} fixedStatus="canceled" onGoToDiscovery={() => setActiveMenu('tenders')} />;
      case 'master-list':
        return <MasterTenderList user={user} canManage={true} onGoToDiscovery={() => setActiveMenu('tenders')} />;
      case 'emds':
        return <EmdList />;
      case 'nogo':
        return <NoGoTenderList user={user} canManage={false} onGoToDiscovery={() => setActiveMenu('tenders')} />;
      case 'archived':
        return <ArchivedTenderList />;
      case 'myTasks':
        return <MyAssignedTasks user={user} />;
      case 'brands':
        return <BrandList user={user} canEdit={true} />;
      case 'clients':
        // Tender Management can now add/edit clients (same as Sanbee Employee)
        return <ClientList user={user} canEdit={true} />;
      case 'dashboard':
      default:
        return (
          <>
            <div className="content-header">
              <h2>Welcome, {user?.full_name || user?.username || 'Tender Manager'}!</h2>
              <p>Manage tenders, EMDs, and view clients.</p>
            </div>
            <UpcomingDeadlinesWidget />
            {dashboardError && (
              <div className="error-message" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ flex: 1 }}>{dashboardError}</span>
                <button type="button" onClick={fetchDashboardData} disabled={dashboardLoading} className="confirm-go-ahead-btn" style={{ padding: '6px 12px' }}>
                  {dashboardLoading ? 'Loading…' : 'Retry'}
                </button>
                <button type="button" onClick={() => setDashboardError(null)} aria-label="Dismiss">×</button>
              </div>
            )}
            <div className="stats-grid">
              {stats.map((stat, index) => (
                <div key={index} className="stat-card">
                  <div className="stat-content">
                    <span className="stat-label">{stat.label}</span>
                    <span className="stat-value">{dashboardLoading ? '…' : stat.value}</span>
                    <span className="stat-change" style={{ color: stat.color }}>{stat.change}</span>
                  </div>
                </div>
              ))}
            </div>
            {!dashboardLoading && dashboardData.stats && (
              <BidValueStatsRow stats={dashboardData.stats} loading={dashboardLoading} />
            )}
            {!dashboardLoading && dashboardData.charts && (
              <div className="charts-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24, marginBottom: 24 }}>
                {dashboardData.charts.participating_by_status && dashboardData.charts.participating_by_status.length > 0 && (
                  <div className="chart-card">
                    <h3>Participating Tenders by Status</h3>
                    <div style={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dashboardData.charts.participating_by_status}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="count" fill="#10b981" name="Count" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
                {dashboardData.charts.emds_by_status && dashboardData.charts.emds_by_status.length > 0 && (
                  <div className="chart-card">
                    <h3>EMDs by Status</h3>
                    <div style={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dashboardData.charts.emds_by_status}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="count" fill="#3b82f6" name="Count" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
                {dashboardData.charts.bid_value?.pipeline_value_by_status && (
                  <PipelineValueChart data={dashboardData.charts.bid_value.pipeline_value_by_status} />
                )}
                {dashboardData.charts.bid_value?.win_rate_by_source && (
                  <WinRateBySourceChart data={dashboardData.charts.bid_value.win_rate_by_source} />
                )}
                {dashboardData.charts.bid_value?.win_rate_by_buyer && (
                  <WinRateByBuyerChart data={dashboardData.charts.bid_value.win_rate_by_buyer} />
                )}
                {dashboardData.charts.tenders_over_time && dashboardData.charts.tenders_over_time.length > 0 && (
                  <div className="chart-card" style={{ gridColumn: '1 / -1' }}>
                    <h3>Tenders Over Time (last 6 months)</h3>
                    <div style={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={dashboardData.charts.tenders_over_time}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="count" stroke="#b60000" name="Total" strokeWidth={2} dot={{ r: 4 }} />
                          <Line type="monotone" dataKey="participating" stroke="#10b981" name="Participating" strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="placeholder-section">
              <div className="placeholder-card clickable" onClick={() => setActiveMenu('tenders')}>
                <h3>Tender Discovery</h3>
                <p>Discover and manage tenders.</p>
                <button className="placeholder-btn active">Manage Tenders</button>
              </div>
              <div className="placeholder-card clickable" onClick={() => setActiveMenu('participating')}>
                <h3>Participating Tenders</h3>
                <p>View and update participating tenders.</p>
                <button className="placeholder-btn active">Participating</button>
              </div>
              <div className="placeholder-card clickable" onClick={() => setActiveMenu('emds')}>
                <h3>EMDs</h3>
                <p>Manage earnest money deposits.</p>
                <button className="placeholder-btn active">Manage EMDs</button>
              </div>
              <div className="placeholder-card clickable" onClick={() => setActiveMenu('clients')}>
                <h3>Client List</h3>
                <p>View company clients.</p>
                <button className="placeholder-btn active">View Clients</button>
              </div>
            </div>
          </>
        );
    }
  };

  return (
    <div className="dashboard-layout">
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)} data-tooltip={sidebarOpen ? 'Collapse' : 'Expand'}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {sidebarOpen ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 18l6-6-6-6" />}
          </svg>
        </button>
        <nav className="sidebar-nav">
          {menuItems.map((item) =>
            item.children ? (
              <div key={item.id} className="nav-group">
                <button
                  className={`nav-item nav-parent ${expandedMenus.includes(item.id) ? 'expanded' : ''} ${item.children.some(child => activeMenu === child.id) ? 'has-active' : ''}`}
                  onClick={() => toggleMenuExpand(item.id)}
                  data-tooltip={item.label}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{renderIcon(item.icon)}</svg>
                  <span>{item.label}</span>
                  <svg className="expand-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {renderIcon(expandedMenus.includes(item.id) ? 'chevronDown' : 'chevronRight')}
                  </svg>
                </button>
                <div className={`nav-children ${expandedMenus.includes(item.id) ? 'expanded' : ''}`}>
                  {item.children.map((child) => (
                    <button key={child.id} className={`nav-item nav-child ${activeMenu === child.id ? 'active' : ''}`} onClick={() => setActiveMenu(child.id)} data-tooltip={child.label}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{renderIcon(child.icon)}</svg>
                      <span>{child.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <button key={item.id} className={`nav-item ${activeMenu === item.id ? 'active' : ''}`} onClick={() => setActiveMenu(item.id)} data-tooltip={item.label}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{renderIcon(item.icon)}</svg>
                <span>{item.label}</span>
              </button>
            )
          )}
        </nav>
      </aside>
      <header className="topbar">
        <div className="topbar-left">
          <img src={smLogo} alt="SM Systems" className="topbar-logo" />
          <div className="topbar-divider"></div>
          <h1 className="page-title">SM Tender Management</h1>
        </div>
        <div className="topbar-right">
          <NotificationBell />
          <ThemeToggle />
          <div className="user-profile-wrapper">
            <button className="user-profile" onClick={() => setProfileDropdown(!profileDropdown)}>
              <div className="avatar">{(user?.full_name || user?.username)?.charAt(0) || 'T'}</div>
              <div className="user-info">
                <span className="user-name">{user?.full_name || user?.username || 'Tender Manager'}</span>
                <span className="user-role">SM Tender Management</span>
              </div>
              <svg className={`dropdown-arrow ${profileDropdown ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
            </button>
            {profileDropdown && (
              <div className="profile-dropdown">
                <button className="dropdown-item" onClick={onLogout}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{renderIcon('logout')}</svg>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <main className={`main-content ${sidebarOpen ? '' : 'expanded'}`}>
        <div className="content">
          <div className="content-title-area">
            {pageInfo.breadcrumb.length > 0 && (
              <nav className="breadcrumb" aria-label="Breadcrumb">
                {pageInfo.breadcrumb.map((label, i) => (
                  <span key={i}>
                    {i > 0 && <span className="breadcrumb-sep"> / </span>}
                    <span>{label}</span>
                  </span>
                ))}
              </nav>
            )}
            <h2 className="content-page-title">{pageInfo.title}</h2>
          </div>
          {renderContent()}
        </div>
      </main>
    </div>
  );
}

export default SMTenderManagementDashboard;
